import { toRaw } from "vue";

import { useDefaultFonts } from "@/vue/composables/fonts";

// Stores
import { useAccountStore, useContactsStore, useIntegrationsStore, useFeaturesStore, useSessionStore } from "@store";

// Modules
import { transformLocaleToUnlayerLocale, unlayerMessages } from "./unlayerI18n";

//I18n
import { getI18nInstance, getCurrentLocale } from "@locales/i18n";

// Types
import type { UnsubscribeLinkOptions } from "@/vue/types/unlayer";
import { GetInitConfigModule } from "../unlayer.types";

// Utils
import { err, ok } from "neverthrow";

export const unsubscribeLinkOptions: UnsubscribeLinkOptions = {
  es: [
    { key: "message1", value: "quiero desuscribirme" },
    { key: "message2", value: "cancelar suscripción" },
    { key: "message3", value: "no volver a recibir estos mensajes" },
    { key: "message4", value: "dejar de recibir estos correos" },
  ],
  pt: [
    { key: "message1", value: "quero anular a inscrição" },
    { key: "message2", value: "cancelar inscrição" },
    { key: "message3", value: "não voltar a receber essas mensagens" },
    { key: "message4", value: "deixar de receber esses correios" },
  ],
  en: [
    { key: "message1", value: "unsubscribe" },
    { key: "message2", value: "cancel my subscription" },
    { key: "message3", value: "stop sending these messages" },
    { key: "message4", value: "remove me from your mailing list" },
  ],
  fr: [
    { key: "message1", value: "je veux me désinscrire" },
    { key: "message2", value: "annuler l'abonnement" },
    { key: "message3", value: "arrête d'envoyer ces messages" },
    { key: "message4", value: "cliquez ici pour vous désabonner" },
  ],
  iw: [
    { key: "message1", value: "אני רוצה לבטל את המנוי" },
    { key: "message2", value: "בטל רישום" },
    { key: "message3", value: "אל תקבל את ההודעות הללו שוב" },
    { key: "message4", value: "להפסיק לקבל את המיילים האלה" },
  ],
};

export const getInitConfig: GetInitConfigModule = async ({ editorId, templateFonts }) => {
  const integrationsStore = useIntegrationsStore();
  const accountStore = useAccountStore();
  const contactsStore = useContactsStore();
  const featuresStore = useFeaturesStore();

  const getFonts = () => {
    const accountFonts = accountStore.getFonts();
    const accountDefaultFonts = accountStore.getDefaultFonts();
    const defaultFonts = useDefaultFonts();

    const fonts = accountFonts?.filter((font) => !font.disabled) ?? [];
    const defaultFontsFiltered = defaultFonts.filter((defaultFont) => {
      const fontFound = accountDefaultFonts?.find((font) => font.id === defaultFont.id);
      if (!fontFound) return true;
      return false;
    });
    const editorFonts = [...fonts, ...defaultFontsFiltered].map((font) => ({
      label: font.name,
      value: `${font.value}${font.alterFont?.value ? "," + font.alterFont?.value : ""}`,
      url: font.url,
    }));

    return editorFonts;
  };

  const { t } = getI18nInstance().global;

  const currentLocale = transformLocaleToUnlayerLocale(getCurrentLocale());
  const sessionStore = useSessionStore();
  const session = toRaw(sessionStore.session);

  const supportedIntegrations = toRaw(integrationsStore.getIntegrations()).filter((integration) =>
    ["tiendanube", "vtex", "jumpseller", "shopify", "tray", "mshops", "meli", "wbuy", "woocommerce"].includes(
      integration,
    ),
  );

  const interests = contactsStore.getInterests();
  const integrations = supportedIntegrations;
  const fields = contactsStore.getFields();
  const accountFonts = getFonts();
  const formattedTemplateFonts = templateFonts?.map((font) => ({
    label: font.name,
    value: font.value,
    url: font.url,
  }));
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const customFields = fields?.filter((field) => field.custom) ?? [];

  const formattedCustomFields = customFields.reduce((mergeTags, field) => {
    mergeTags[field.tag.toLowerCase()] = { name: field.name, value: `\${contact.${field.tag.toLowerCase()}}` };
    return mergeTags;
  }, {});

  const interestLinkType = [
    interests
      ? {
          name: "web_interest",
          label: t("linkTypes.web_interest.label"),
          attrs: {
            href: "{{href}}",
            target: "_blank",
            "data-interests": "{{interest}}",
          },
          fields: [
            {
              name: "href",
              label: "URL",
              defaultValue: "",
              inputType: "text" as const,
              placeholderText: "https://...",
            },
            {
              name: "interest",
              label: t("linkTypes.web_interest.fields.interest"),
              defaultValue: "",
              options: interests.map((i) => ({ value: `int_${i.id}`, label: i.name })),
            },
          ],
        }
      : {},
  ];

  const useUnlayerNext = featuresStore.features?.USE_NEXT_UNLAYER_VERSION === "1";
  const useDesignMode = featuresStore.features?.USE_UNLAYER_DESIGN_MODE === "1";
  const useUnlayerHtmlTool = featuresStore.features?.UNLAYER_ALLOW_HTML_TOOL === "1";

  const unlayerUrl = useUnlayerNext ? window.config.unlayerCustomToolsURLNext : window.config.unlayerCustomToolsURL;

  const customCSSUrl = `${unlayerUrl}/unlayer-custom-tools.css?cache=${new Date().getTime()}`;
  const customJSUrl = `${unlayerUrl}/unlayer-custom-tools.js?cache=${new Date().getTime()}`;

  // Previous "1.218.0" - 21/07/2025
  // Previous "1.259.0" - 13/11/2025
  // Current "1.309.4" - 13/11/2025  - Rollback a 1.259.0 desde error en api timers y otros posibles issues(?)
  const unlayerVersion = useUnlayerNext ? "1.259.0" : "1.259.0";

  // const customCSS = await fetch(customCSSUrl);
  // const customJS = await fetch(customJSUrl);
  // const customCSSCode = await customCSS.text();
  // const customJSCode = await customJS.text();

  let customJSCode = "";
  let customCSSCode = "";

  try {
    const customJS = await fetch(customJSUrl);
    const customCSS = await fetch(customCSSUrl);

    if (!customJS.ok || !customCSS.ok) {
      return err({
        type: "CUSTOM_TOOLS_FETCH_ERROR",
        message: `Error al cargar JS: ${customJS.status} ${customJS.statusText} - Error al cargar CSS: ${customCSS.status} ${customCSS.statusText}`,
        version: unlayerVersion,
      });
    }

    customJSCode = await customJS.text();

    const rawCSSCode = await customCSS.text();

    const removeAllComments = (cssCode: string): string => {
      return cssCode.replace(/\/\*[\s\S]*?\*\//g, "");
    };

    customCSSCode = removeAllComments(rawCSSCode);
  } catch (error) {
    return err({
      type: "CUSTOM_TOOLS_FETCH_ERROR",
      message: `Error al cargar custom tools: ${error instanceof Error ? error.message : "Error desconocido"}`,
      version: unlayerVersion,
    });
  }

  console.log("is Unlayer Next", useUnlayerNext);
  console.log("is Unlayer Design Mode enabled", useDesignMode);
  console.log("Unlayer version", unlayerVersion);

  return ok({
    version: unlayerVersion,
    id: editorId,
    projectId: 77966,
    displayMode: "email",
    safeHtml: true,
    designMode: useDesignMode ? "edit" : "live",
    features: {
      textEditor: {
        spellChecker: true,
        tables: true,
        cleanPaste: true,
      },
      preheaderText: false,
      preview: false,
      imageEditor: true,
      userUploads: true,
      stockImages: {
        enabled: true,
        safeSearch: true,
        defaultSearchTerm: "product",
      },
      audit: true,
      ai: true,
      smartMergeTags: false,
    },
    mergeTagsConfig: {
      sort: false,
    },
    appearance: {
      theme: "light",
      panels: {
        tools: {
          dock: "left",
        },
      },
      loader: {
        html: '<svg class="p-spinner" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle></svg>',
        css: ".p-spinner{animation:p-rotate 2s linear infinite;z-index:2;width:40px;height:40px}.p-spinner circle{stroke:#88B9F4;stroke-linecap:round;animation:p-dash 1.5s ease-in-out infinite}@keyframes p-rotate{100%{transform:rotate(360deg)}}@keyframes p-dash{0%{stroke-dasharray:1,150;stroke-dashoffset:0}50%{stroke-dasharray:90,150;stroke-dashoffset:-35}100%{stroke-dasharray:90,150;stroke-dashoffset:-124}}",
      },
    },
    editor: {
      autoSelectOnDrop: true,
    },
    fonts: {
      showDefaultFonts: false,
      customFonts: formattedTemplateFonts ? [...formattedTemplateFonts, ...accountFonts] : accountFonts,
    },
    specialLinks: [
      {
        name: t("specialLinks.unsubscribe"),
        href: "${urls.unsubscribe}",
        target: "_blank",
      },
      {
        name: t("specialLinks.share"),
        href: "${urls.share.selector}",
        target: "_blank",
      },
      {
        name: t("specialLinks.onlineVersion"),
        href: "${urls.online_version}",
        target: "_blank",
      },
    ],
    linkTypes: [
      {
        name: "sms",
        enabled: false,
      },
      {
        name: "web",
        label: t("linkTypes.web.label"),
        attrs: {
          href: "{{href}}",
          target: "_blank",
        },
        fields: [
          {
            name: "href",
            label: "URL",
            defaultValue: "",
            inputType: "text",
            placeholderText: "https://...",
          },
        ],
      },
      {
        name: "wapp",
        label: t("linkTypes.whatsapp.label"),
        attrs: {
          href: "https://wa.me/{{number}}?text={{message}}",
          target: "_blank",
        },
        fields: [
          {
            name: "number",
            label: t("linkTypes.whatsapp.fields.phoneLabel"),
            defaultValue: "",
            inputType: "number",
            placeholderText: null,
            options: [],
          },
          {
            name: "message",
            label: t("linkTypes.whatsapp.fields.messageLabel"),
            defaultValue: "",
            inputType: "textarea",
            placeholderText: null,
          },
        ],
      },
      ...interestLinkType,
    ],
    mergeTags: {
      first_name: {
        name: t("mergeTags.first_name"),
        value: "${contact.first_name}",
        sample: "[NOMBRE]",
      },
      last_name: {
        name: t("mergeTags.last_name"),
        value: "${contact.last_name}",
        sample: "[APELLIDO]",
      },
      email: {
        name: t("mergeTags.email"),
        value: "${contact.email}",
        sample: "[EMAIL]",
      },
      ...formattedCustomFields,
    },
    designTags: {
      business_name: session?.account.name,
      postal_address: session?.account.postalAddress,
    },
    customCSS: [customCSSCode],
    customJS: [customJSCode],

    tools: {
      heading: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='33' height='42' viewBox='0 0 33 42' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='32' y='41' width='31' height='40' rx='5' transform='rotate(-180 32 41)' stroke='%23737373' stroke-width='2'/%3E%3Cpath d='M24 8V12.601H23.5567C23.2939 11.5396 23.0025 10.779 22.6823 10.3193C22.3621 9.85128 21.9228 9.47935 21.3645 9.20354C21.0525 9.0531 20.5066 8.97788 19.7266 8.97788H18.4828V22.0914C18.4828 22.9607 18.5279 23.5039 18.6182 23.7212C18.7167 23.9385 18.9015 24.1308 19.1724 24.2979C19.4516 24.4567 19.8292 24.5361 20.3054 24.5361H20.8596V25H12.1158V24.5361H12.67C13.1544 24.5361 13.5443 24.4484 13.8399 24.2729C14.0534 24.1559 14.2217 23.9553 14.3448 23.6711C14.4351 23.4705 14.4803 22.944 14.4803 22.0914V8.97788H13.2734C12.1486 8.97788 11.3317 9.22026 10.8227 9.70501C10.1084 10.382 9.65681 11.3473 9.46798 12.601H9V8H24Z' fill='%23737373'/%3E%3Cpath d='M8 30L25 30' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8 35L25 35' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
        position: 1,
        properties: {
          text: {
            value: t("tools.heading.text"),
          },
          textAlign: {
            value: "center",
          },
        },
      },
      text: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.3031 19.1905L12.837 13.1248H4.80875L2.35642 19.1905H0L7.83648 0H9.9326L17.7143 19.1905H15.3031ZM12.1383 11.1509L9.7956 5.12443C9.7408 4.96756 9.64947 4.71047 9.5216 4.35315C9.40287 3.99584 9.27956 3.62545 9.1517 3.24199C9.02383 2.85853 8.91879 2.54914 8.83659 2.31384C8.74526 2.67115 8.64479 3.02847 8.53519 3.38579C8.43472 3.73439 8.33425 4.0612 8.23379 4.36623C8.13332 4.66254 8.04655 4.91527 7.97348 5.12443L5.58966 11.1509H12.1383Z' fill='%23737373'/%3E%3Cpath d='M20.6665 8.85715H30.9999M20.6665 16.2381H30.9999M1.47607 23.6191H30.9999M1.47607 31H30.9999M20.6665 1.4762H30.9999' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
        position: 2,
        properties: {
          text: {
            value: t("tools.text.text"),
          },
        },
      },
      image: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='34' height='34' viewBox='0 0 34 34' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M32.4172 24.4675L29.438 21.4882C28.1705 20.2207 26.1154 20.2207 24.8478 21.4882L22.2047 24.1313L25.6881 27.6147C26.005 27.9316 26.005 28.4454 25.6881 28.7623C25.3712 29.0792 24.8575 29.0792 24.5406 28.7623L15.2379 19.4596C13.9704 18.1921 11.9153 18.1921 10.6478 19.4596L1.58276 28.5246V26.2295L9.50021 18.3121C11.4015 16.4108 14.4841 16.4108 16.3855 18.3121L21.0572 22.9838L23.7003 20.3407C25.6016 18.4394 28.6842 18.4394 30.5855 20.3407L32.4172 22.1724V24.4675Z' fill='%23737373'/%3E%3Ccircle cx='21.8686' cy='11.1484' r='2.4343' fill='%23737373'/%3E%3Cpath d='M29 1H5C2.79086 1 1 2.79086 1 5V29C1 31.2091 2.79086 33 5 33H29C31.2091 33 33 31.2091 33 29V5C33 2.79086 31.2091 1 29 1Z' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
        position: 3,
        properties: {},
      },
      button: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='50' height='31' viewBox='0 0 50 31' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='1' y='1' width='48' height='21.6364' rx='10.8182' stroke='%23737373' stroke-width='2'/%3E%3Cpath d='M38.8119 27.7744L37.8735 17.0121L46.7248 23.2059L41.7894 23.7945L38.8119 27.7744Z' fill='%23737373'/%3E%3Cpath d='M41.7894 23.7945L38.8119 27.7744L37.8735 17.0121L46.7248 23.2059L41.7894 23.7945ZM41.7894 23.7945L45.0526 29.4466' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cline x1='10' y1='11.3182' x2='40.9091' y2='11.3182' stroke='%23737373'/%3E%3C/svg%3E%0A",
        position: 4,
        properties: {
          text: {
            value: t("tools.button.text"),
          },
          buttonColors: {
            value: {
              color: "#FFFFFF",
              backgroundColor: "#0059d5",
              hoverColor: "#FFFFFF",
              hoverBackgroundColor: "#0059d5",
            },
          },
        },
      },
      divider: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='52' height='28' viewBox='0 0 52 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cmask id='path-1-inside-1_2313_13742' fill='white'%3E%3Cpath d='M46 28L6 28L6 20L46 20L46 28Z'/%3E%3C/mask%3E%3Cpath d='M46 20L6 20L6 36L46 36L46 20Z' fill='%23E5E5E5' mask='url(%23path-1-inside-1_2313_13742)'/%3E%3Cmask id='path-3-inside-2_2313_13742' fill='white'%3E%3Cpath d='M6 0L46 0V8L6 8L6 0Z'/%3E%3C/mask%3E%3Cpath d='M6 8L46 8V-8L6 -8L6 8Z' fill='%23E5E5E5' mask='url(%23path-3-inside-2_2313_13742)'/%3E%3Cline y1='14' x2='52' y2='14' stroke='%23737373' stroke-width='2'/%3E%3C/svg%3E%0A",
        position: 5,
        properties: {},
      },
      menu: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='37' height='21' viewBox='0 0 37 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.00008 1H36M1 10.1875H35.9999M1.00008 19.375H18.5H36' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
        position: 6,
        properties: {},
      },
      video: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='34' height='31' viewBox='0 0 34 31' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.11111 1V29.4444M25.8889 1V29.4444M1 8.11111H8.11111M25.8889 8.11111H33M1 15.2222H8.11111M33 15.2222H25.8889M1 22.3333H8.11111M25.8889 22.3333H33M2.77778 29.4444H31.2222C32.2041 29.4444 33 28.6485 33 27.6667V2.77778C33 1.79594 32.2041 1 31.2222 1H2.77778C1.79594 1 1 1.79594 1 2.77778V27.6667C1 28.6485 1.79594 29.4444 2.77778 29.4444Z' stroke='%23737373' stroke-width='2'/%3E%3Cpath d='M19.1836 14.8677L16.0833 12.8008C15.4389 12.3712 14.5757 12.8332 14.5757 13.6077V17.7415C14.5757 18.516 15.4389 18.9779 16.0833 18.5483L19.1836 16.4814C19.7593 16.0976 19.7593 15.2516 19.1836 14.8677Z' fill='%23737373'/%3E%3C/svg%3E%0A",
        position: 7,
        properties: {},
      },
      social: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='41' height='38' viewBox='0 0 41 38' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M17 29.5522C17 24.8288 13.1946 21 8.5 21C3.80545 21 0 24.8288 0 29.5522C0 33.8206 3.10845 37.3586 7.1723 38V32.0246H5.0133V29.5522H7.1723V27.6681C7.1723 25.525 8.4405 24.3413 10.3828 24.3413C11.3127 24.3413 12.285 24.5081 12.285 24.5081V26.6119H11.2141C10.1575 26.6119 9.82855 27.2713 9.82855 27.9478V29.5522H12.1856L11.809 32.0237H9.82855V38C13.8916 37.3586 17 33.8206 17 29.5522Z' fill='%23737373'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M32.7677 21C34.8333 21 35.1341 21.011 36.0045 21.051C36.9089 21.0926 37.5269 21.2363 38.0675 21.4463C38.6349 21.6595 39.1489 21.994 39.5737 22.4263C40.006 22.8511 40.3405 23.3651 40.5538 23.9325C40.7637 24.4731 40.9074 25.0911 40.949 25.9955C40.9898 26.9024 41 27.1914 41 29.5V29.568C41 31.8146 40.9898 32.1069 40.949 33.0045C40.9074 33.9089 40.7637 34.5269 40.5538 35.0675C40.3405 35.6349 40.006 36.1489 39.5737 36.5737C39.1489 37.006 38.6349 37.3405 38.0675 37.5538C37.5269 37.7637 36.9089 37.9074 36.0045 37.949C35.0976 37.9898 34.8086 38 32.5 38H32.432C30.1855 38 29.8931 37.9898 28.9955 37.949C28.0911 37.9074 27.4731 37.7637 26.9325 37.5538C26.3651 37.3405 25.8511 37.006 25.4263 36.5737C24.994 36.1489 24.6595 35.6349 24.4463 35.0675C24.2363 34.5269 24.0926 33.9089 24.051 33.0045C24.011 32.1341 24 31.8324 24 29.7677V29.2323C24 27.1668 24.011 26.8658 24.051 25.9955C24.0926 25.0911 24.2363 24.4731 24.4463 23.9325C24.6595 23.3651 24.994 22.8511 25.4263 22.4263C25.8511 21.994 26.3651 21.6595 26.9325 21.4463C27.4731 21.2363 28.0911 21.0926 28.9955 21.051C29.8658 21.011 30.1676 21 32.2323 21H32.7677ZM32.6989 22.5317H32.3011C30.2135 22.5317 29.9347 22.541 29.0652 22.581C28.2364 22.6192 27.7868 22.757 27.4867 22.8734C27.0898 23.0281 26.8067 23.2117 26.5092 23.5092C26.2117 23.8067 26.0281 24.0898 25.8734 24.4867C25.757 24.7868 25.6184 25.2364 25.581 26.0652C25.541 26.9347 25.5317 27.2135 25.5317 29.3011V29.6989C25.5317 31.7865 25.541 32.0653 25.581 32.9349C25.6192 33.7636 25.757 34.2133 25.8734 34.5133C26.0281 34.9094 26.2126 35.1933 26.5092 35.4908C26.8067 35.7883 27.0898 35.9719 27.4867 36.1266C27.7868 36.243 28.2364 36.3816 29.0652 36.419C29.9611 36.4598 30.2297 36.4683 32.5 36.4683H32.568C34.7754 36.4683 35.0475 36.4598 35.934 36.419C36.7636 36.3807 37.2133 36.243 37.5133 36.1266C37.9094 35.9719 38.1933 35.7883 38.4908 35.4908C38.7883 35.1933 38.9719 34.9103 39.1266 34.5133C39.243 34.2133 39.3816 33.7636 39.419 32.9349C39.4598 32.0381 39.4683 31.7704 39.4683 29.5V29.432C39.4683 27.2246 39.4598 26.9526 39.419 26.066C39.3807 25.2364 39.243 24.7868 39.1266 24.4867C38.9904 24.1174 38.7731 23.7834 38.4908 23.5092C38.2166 23.2269 37.8826 23.0096 37.5133 22.8734C37.2133 22.757 36.7636 22.6184 35.9349 22.581C35.0653 22.541 34.7865 22.5317 32.6989 22.5317ZM32.5 25.1353C33.0732 25.1353 33.6408 25.2481 34.1703 25.4675C34.6999 25.6868 35.181 26.0084 35.5863 26.4137C35.9916 26.819 36.3132 27.3001 36.5325 27.8297C36.7519 28.3592 36.8648 28.9268 36.8648 29.5C36.8648 30.0732 36.7519 30.6408 36.5325 31.1703C36.3132 31.6999 35.9916 32.181 35.5863 32.5863C35.181 32.9916 34.6999 33.3132 34.1703 33.5325C33.6408 33.7519 33.0732 33.8648 32.5 33.8648C31.3424 33.8648 30.2322 33.4049 29.4137 32.5863C28.5951 31.7678 28.1353 30.6576 28.1353 29.5C28.1353 28.3424 28.5951 27.2322 29.4137 26.4137C30.2322 25.5951 31.3424 25.1353 32.5 25.1353ZM32.5 26.667C31.7486 26.667 31.028 26.9654 30.4967 27.4967C29.9654 28.028 29.667 28.7486 29.667 29.5C29.667 30.2514 29.9654 30.972 30.4967 31.5033C31.028 32.0346 31.7486 32.3331 32.5 32.3331C33.2514 32.3331 33.972 32.0346 34.5033 31.5033C35.0346 30.972 35.3331 30.2514 35.3331 29.5C35.3331 28.7486 35.0346 28.028 34.5033 27.4967C33.972 26.9654 33.2514 26.667 32.5 26.667ZM37.0373 23.9427C37.3078 23.9427 37.5673 24.0502 37.7585 24.2415C37.9498 24.4327 38.0573 24.6922 38.0573 24.9627C38.0573 25.2332 37.9498 25.4927 37.7585 25.6839C37.5673 25.8752 37.3078 25.9827 37.0373 25.9827C36.7668 25.9827 36.5073 25.8752 36.3161 25.6839C36.1248 25.4927 36.0173 25.2332 36.0173 24.9627C36.0173 24.6922 36.1248 24.4327 36.3161 24.2415C36.5073 24.0502 36.7668 23.9427 37.0373 23.9427Z' fill='%23737373'/%3E%3Cpath d='M5.3465 15C11.7614 15 15.2703 9.99865 15.2703 5.66195C15.2703 5.51958 15.2702 5.37801 15.26 5.23804C15.9428 4.77296 16.5319 4.19715 17 3.5376C16.3634 3.80314 15.6881 3.97731 14.9966 4.05429C15.7247 3.64402 16.2696 2.99879 16.53 2.23867C15.8454 2.6209 15.0966 2.89032 14.3157 3.0353C13.7899 2.50882 13.0943 2.16015 12.3366 2.04327C11.5789 1.92639 10.8014 2.04783 10.1245 2.38877C9.44752 2.72972 8.90892 3.27115 8.59205 3.92924C8.27518 4.58734 8.19771 5.32537 8.37165 6.02908C6.98495 5.96368 5.62836 5.62462 4.38995 5.0339C3.15154 4.44318 2.05898 3.614 1.1832 2.60019C0.73722 3.3226 0.6006 4.1778 0.801155 4.99166C1.00171 5.80553 1.52436 6.51688 2.2627 6.98088C1.70787 6.96576 1.16504 6.82531 0.68 6.57136V6.61295C0.680331 7.37063 0.959136 8.10489 1.46914 8.69121C1.97914 9.27752 2.68895 9.67982 3.4782 9.82988C2.96464 9.96163 2.42581 9.98078 1.90315 9.88587C2.12599 10.5381 2.55993 11.1085 3.14426 11.5172C3.72859 11.9259 4.43409 12.1526 5.16205 12.1654C4.4388 12.7001 3.61062 13.0955 2.72487 13.3288C1.83912 13.5621 0.913175 13.6288 0 13.5251C1.59511 14.4884 3.45116 14.9993 5.3465 14.9968' fill='%23737373'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M37.7817 12.7817H35.5583V9.3C35.5583 8.47083 35.5442 7.4025 34.4025 7.4025C33.2442 7.4025 33.0683 8.3075 33.0683 9.24167V12.7817H30.845V5.62333H32.9775V6.60167H33.0083C33.305 6.03917 34.0308 5.44583 35.1133 5.44583C37.3658 5.44583 37.7825 6.9275 37.7825 8.85583V12.7817H37.7817ZM28.3375 4.64583C28.1681 4.646 28.0003 4.61279 27.8437 4.54812C27.6872 4.48344 27.5449 4.38856 27.425 4.26889C27.3051 4.14921 27.2099 4.0071 27.1449 3.85065C27.0799 3.6942 27.0464 3.52649 27.0462 3.35708C27.0461 3.18768 27.0793 3.0199 27.144 2.86333C27.2086 2.70675 27.3035 2.56445 27.4232 2.44455C27.5429 2.32465 27.685 2.22949 27.8414 2.16451C27.9979 2.09953 28.1656 2.066 28.335 2.06583C28.6771 2.0655 29.0054 2.20109 29.2475 2.44278C29.4897 2.68447 29.6259 3.01245 29.6262 3.35458C29.6266 3.69671 29.491 4.02496 29.2493 4.26712C29.0076 4.50927 28.6796 4.6455 28.3375 4.64583ZM27.2233 12.7817H29.45V5.62333H27.2225V12.7817H27.2233ZM38.89 0H26.1067C25.4958 0 25 0.484167 25 1.08167V13.9175C25 14.515 25.4958 15 26.1067 15H38.89C39.5017 15 40 14.515 40 13.9175V1.08167C40 0.484167 39.5017 0 38.89 0Z' fill='%23737373'/%3E%3C/svg%3E%0A",
        position: 8,
        properties: {},
      },
      timer: {
        icon: "data:image/svg+xml;charset=UTF-8,%3csvg width='29' height='34' viewBox='0 0 29 34' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M14.0909 14.0908V19.909L18.4545 24.2727M27.1818 19.909C27.1818 27.1389 21.3208 32.9999 14.0909 32.9999C6.861 32.9999 1 27.1389 1 19.909C1 12.6791 6.861 6.81812 14.0909 6.81812C21.3208 6.81812 27.1818 12.6791 27.1818 19.909Z' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M24.2729 10.3296L26.33 8.27255' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M14.0908 1L14.0908 6.81818' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M25.7271 6.81812L27.7841 8.87515' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M4.51123 10.3296L2.45419 8.27255' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M1 8.875L3.05704 6.81796' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3cpath d='M18.4543 2.45455V1.72727C18.4543 1.32561 17.8031 1 16.9998 1H11.1816C10.3783 1 9.72705 1.32561 9.72705 1.72727V2.45455' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e",
        position: 9,
        properties: {
          countdown: {
            value: {
              timezone: timezone,
            },
          },
        },
      },
      html: {
        enabled: useUnlayerHtmlTool,
        position: 10,
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='34' height='34' viewBox='0 0 34 34' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M29 1H5C2.79086 1 1 2.79086 1 5V29C1 31.2091 2.79086 33 5 33H29C31.2091 33 33 31.2091 33 29V5C33 2.79086 31.2091 1 29 1Z' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M15.2 26L18.8 8M22.4 12.5L26 17L22.4 21.5M11.6 21.5L8 17L11.6 12.5' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
        properties: {
          html: {
            value: t("tools.html.html"),
          },
        },
      },
      "custom#products": {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='35' height='39' viewBox='0 0 35 39' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M22.595 22.2974V17.2502L30.3481 12.8481V19.2051C30.3481 19.4712 30.2034 19.7172 29.9689 19.8501L23.7302 22.9424C23.4953 23.0715 23.2087 23.0698 22.976 22.9372C22.7429 22.8051 22.5981 22.5618 22.595 22.2974Z' fill='%23737373'/%3E%3Cpath d='M34.7441 8.65654C34.4855 8.11893 34.0492 7.6814 33.514 7.42468L29.2731 5.38242C29.2601 5.37463 29.2481 5.36857 29.2354 5.36425L18.6358 0.260438C17.915 -0.0868127 17.087 -0.0868127 16.3663 0.260438L1.48808 7.42467C0.856481 7.72867 0.380772 8.26107 0.148694 8.9227C0.0310573 9.25871 -0.0152482 9.60567 0.00517647 9.94944C0.00431369 9.9581 0 9.96329 0 9.97108V28.9709C0 29.976 0.584141 30.9065 1.48693 31.3406L16.3659 38.5057C16.7257 38.6787 17.113 38.7658 17.5005 38.7658C17.8879 38.7658 18.2736 38.6787 18.6342 38.5057L33.5139 31.3423C34.4167 30.9073 35 29.9769 35 28.9718V9.97196C35 9.94601 34.9862 9.9238 34.9845 9.8987C35.0043 9.48252 34.9356 9.05797 34.7441 8.65654ZM32.7711 8.9787C32.9557 9.06753 33.1061 9.21808 33.1953 9.40238C33.4101 9.85202 33.2203 10.3948 32.7711 10.6117L17.8921 17.7751C17.6448 17.8957 17.3595 17.8948 17.1104 17.7751L11.9161 15.2737L28.8832 7.10465L32.7711 8.9787ZM9.56323 14.4961C9.39929 14.5737 9.25836 14.7038 9.16805 14.8768C9.15683 14.8976 9.14734 14.9189 9.13785 14.9406C9.08982 15.0551 9.06825 15.1739 9.07083 15.2922L9.07112 19.8133L6.97844 18.8056V13.981L25.0713 5.27148L26.8967 6.15085L9.56323 14.4961ZM1.76961 9.49473C1.84957 9.26659 2.01351 9.08287 2.2318 8.97789L17.1107 1.81365C17.3598 1.69396 17.6442 1.69396 17.8933 1.81365L23.0885 4.31509L6.12073 12.4856L2.23195 10.6121C2.0473 10.5232 1.89688 10.3727 1.80772 10.1875C1.70303 9.96861 1.69009 9.72316 1.77005 9.49474L1.76961 9.49473ZM2.23094 29.7896C1.92003 29.6379 1.71813 29.3183 1.71813 28.9723V12.2775L5.26064 13.9824V19.3481C5.26064 19.6798 5.45046 19.9803 5.74757 20.1234L9.55816 21.9591C9.67579 22.0159 9.80291 22.0445 9.93004 22.0445C10.09 22.0445 10.2487 21.9998 10.3879 21.9127C10.6379 21.754 10.7891 21.4803 10.7891 21.183V16.6433L16.3656 19.3291C16.4551 19.3712 16.5494 19.3928 16.6423 19.4248V36.7272L2.23094 29.7896ZM32.7715 29.7896L18.3608 36.7292V19.4261C18.452 19.3935 18.5472 19.3718 18.6366 19.3303L20.7402 18.3166L27.0123 15.297L33.2844 12.2774V28.9721C33.2852 29.3182 33.0841 29.6379 32.7715 29.7896Z' fill='%23737373'/%3E%3C/svg%3E%0A",
        position: 11,
        data: {
          sessionData: session,
          config: window.config,
        },
        properties: {
          productLibrary: {
            editor: {
              data: {
                integrations: integrations,
              },
            },
          },
          layoutCode: {
            enabled: false,
          },
          layoutSelector: {
            editor: {
              data: {
                options: [
                  { label: "layout1", value: "layout1" },
                  { label: "layout2", value: "layout2" },
                  { label: "layout3", value: "layout3" },
                  { label: "layout4", value: "layout4" },
                  // { label: "layout5", value: "layout5" },
                  // { label: "layout6", value: "layout6" },
                  // { label: "layout7", value: "layout7" },
                  // { label: "layout8", value: "layout8" },
                ],
              },
            },
          },
        },
      },
      "custom#fileButton": {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='27' height='34' viewBox='0 0 27 34' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.11111 18.7778H18.7778M13.4444 13.4444V24.1111M22.3333 33H4.55556C2.59188 33 1 31.4081 1 29.4444V4.55556C1 2.59188 2.59188 1 4.55556 1H14.4858C14.9573 1 15.4095 1.1873 15.7429 1.5207L25.3682 11.146C25.7016 11.4794 25.8889 11.9316 25.8889 12.403V29.4444C25.8889 31.4081 24.297 33 22.3333 33Z' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E",
        position: 12,
        properties: {
          fileUpload: {
            editor: {
              data: {
                sessionData: session,
                config: window.config,
              },
            },
          },
        },
      },
      "custom#footer": {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='31' height='42' viewBox='0 0 31 42' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cmask id='path-1-inside-1_2323_13474' fill='white'%3E%3Cpath d='M3.75757 35C3.75757 33.6638 4.84075 32.5806 6.17692 32.5806H24.8231C26.1592 32.5806 27.2424 33.6638 27.2424 35C27.2424 36.3362 26.1592 37.4193 24.8231 37.4193H6.17692C4.84075 37.4193 3.75757 36.3362 3.75757 35Z'/%3E%3C/mask%3E%3Cpath d='M3.75757 32.5806H27.2424H3.75757ZM27.2424 35C27.2424 40.7544 22.5775 45.4193 16.8231 45.4193H14.1769C8.42247 45.4193 3.75757 40.7544 3.75757 35V35C3.75757 34.3583 4.01246 33.743 4.46618 33.2892C4.9199 32.8355 5.53527 32.5806 6.17692 32.5806V32.5806H3.75757H24.8231C25.4647 32.5806 26.0801 32.8355 26.5338 33.2892C26.9875 33.743 27.2424 34.3583 27.2424 35V35ZM3.75757 37.4193V32.5806V37.4193ZM27.2424 32.5806V37.4193V32.5806Z' fill='%23737373' mask='url(%23path-1-inside-1_2323_13474)'/%3E%3Crect x='1' y='1' width='29' height='40' rx='5' stroke='%23737373' stroke-width='2'/%3E%3C/svg%3E%0A",
        position: 13,
        data: {
          sessionData: session,
          config: window.config,
          isTiendanube: integrationsStore.isTiendanube,
        },
        properties: {
          showBrand: {
            editor: {
              data: {
                sessionData: session,
                config: window.config,
              },
            },
          },
          languageSelector: {
            editor: {
              data: {
                options: [
                  { label: "Español", value: "es" },
                  { label: "Português", value: "pt" },
                  { label: "English", value: "en" },
                  { label: "Français", value: "fr" },
                  { label: "עִברִית", value: "iw" },
                ],
              },
            },
          },
          unsubscribeLink: {
            editor: {
              data: {
                options: unsubscribeLinkOptions,
              },
            },
          },
          fontSize: {
            editor: {
              data: {
                options: [
                  { label: "11", value: "11" },
                  { label: "12", value: "12" },
                  { label: "13", value: "13" },
                  { label: "14", value: "14" },
                  { label: "15", value: "15" },
                  { label: "16", value: "16" },
                ],
              },
            },
          },
        },
      },
    },
    tabs: {
      content: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='27' height='28' viewBox='0 0 27 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19.5518 0.965576L26.0022 12.138H13.1014L19.5518 0.965576Z' fill='%23737373'/%3E%3Ccircle cx='6.05172' cy='20.9829' r='6.05172' fill='%23737373'/%3E%3Crect x='14.8965' y='14.9312' width='11.1724' height='11.1724' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
      blocks: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.42857C0 0.639593 0.639593 0 1.42857 0H18.5714C19.3604 0 20 0.639593 20 1.42857V4.28571C20 5.07469 19.3604 5.71429 18.5714 5.71429H1.42857C0.639594 5.71429 0 5.07469 0 4.28571V1.42857Z' fill='%23737373'/%3E%3Cpath d='M0 10C0 9.21102 0.639593 8.57143 1.42857 8.57143H10C10.789 8.57143 11.4286 9.21102 11.4286 10V18.5714C11.4286 19.3604 10.789 20 10 20H1.42857C0.639593 20 0 19.3604 0 18.5714V10Z' fill='%23737373'/%3E%3Cpath d='M15.7143 8.57143C14.9253 8.57143 14.2857 9.21102 14.2857 10V18.5714C14.2857 19.3604 14.9253 20 15.7143 20H18.5714C19.3604 20 20 19.3604 20 18.5714V10C20 9.21102 19.3604 8.57143 18.5714 8.57143H15.7143Z' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
      body: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.0339095 13.4026C0.325839 16.6004 1.81288 19.5707 4.19849 21.7202C6.58411 23.8697 9.69257 25.0405 12.9034 24.9989L13.0158 24.9989L13.0156 24.9989C13.8741 25.0056 14.6997 24.6694 15.3091 24.0646C15.9185 23.4598 16.2612 22.6369 16.2612 21.7785L16.2612 20.006C16.2612 19.344 16.5243 18.7089 16.9923 18.2406C17.4606 17.7725 18.0955 17.5094 18.7577 17.5094L21.2543 17.5094L21.2541 17.5094C22.2473 17.5094 23.1998 17.1149 23.9021 16.4128C24.6043 15.7104 24.9988 14.7579 24.9988 13.7647L24.9988 12.9033L24.9988 12.9035C25.0404 9.69266 23.8696 6.58452 21.7201 4.19858C19.5706 1.81296 16.6005 0.325924 13.4025 0.0339948C11.0094 -0.143003 8.61578 0.373783 6.50894 1.52205C4.40209 2.67032 2.67036 4.40187 1.52206 6.50893C0.373761 8.61599 -0.143014 11.0095 0.034005 13.4025L0.0339095 13.4026ZM10.0195 18.1335C10.0195 18.63 9.82227 19.1063 9.4712 19.4575C9.12013 19.8086 8.64388 20.0058 8.14717 20.0058C7.65066 20.0058 7.17439 19.8086 6.82335 19.4575C6.47207 19.1062 6.27479 18.63 6.27479 18.1335C6.27479 17.637 6.47206 17.1607 6.82335 16.8094C7.17442 16.4584 7.65067 16.2611 8.14717 16.2611C8.64389 16.2611 9.12016 16.4584 9.4712 16.8094C9.82227 17.1607 10.0195 17.637 10.0195 18.1335ZM5.65081 12.5164C5.15409 12.5164 4.67782 12.3191 4.32678 11.968C3.97571 11.6167 3.77844 11.1405 3.77844 10.644C3.77844 10.1475 3.97571 9.67121 4.32678 9.31995C4.67785 8.96888 5.1541 8.77161 5.65081 8.77161C6.14732 8.77161 6.62359 8.96888 6.97463 9.31995C7.32591 9.67123 7.52319 10.1475 7.52319 10.644C7.52319 11.1405 7.32592 11.6168 6.97463 11.968C6.62356 12.3191 6.14731 12.5164 5.65081 12.5164ZM21.2536 9.39581C21.2536 9.89232 21.0563 10.3686 20.7052 10.7196C20.3542 11.0709 19.8779 11.2682 19.3812 11.2682C18.8847 11.2682 18.4084 11.0709 18.0574 10.7196C17.7061 10.3686 17.5088 9.89231 17.5088 9.39581C17.5088 8.89908 17.7061 8.42282 18.0574 8.07178C18.4084 7.72071 18.8847 7.52344 19.3812 7.52344C19.8779 7.52344 20.3542 7.72071 20.7052 8.07178C21.0563 8.42284 21.2536 8.8991 21.2536 9.39581ZM13.7641 5.65106C13.7641 6.14758 13.5668 6.62384 13.2157 6.97488C12.8647 7.32616 12.3884 7.52344 11.8917 7.52344C11.3952 7.52344 10.9189 7.32617 10.5679 6.97488C10.2166 6.62382 10.0193 6.14756 10.0193 5.65106C10.0193 5.15434 10.2166 4.67807 10.5679 4.32703C10.9189 3.97596 11.3952 3.77869 11.8917 3.77869C12.3884 3.77869 12.8647 3.97596 13.2157 4.32703C13.5668 4.6781 13.7641 5.15435 13.7641 5.65106Z' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
      images: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='23' height='20' viewBox='0 0 23 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.42857 0C4.08731 0 3 1.08731 3 2.42857V14.5714C3 15.9127 4.08731 17 5.42857 17H20C21.3413 17 22.4286 15.9127 22.4286 14.5714V2.42857C22.4286 1.08731 21.3413 0 20 0H5.42857ZM20 14.5714H5.42857L10.2857 4.85714L13.9286 12.1429L16.3571 7.28571L20 14.5714Z' fill='%23737373'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M21.8571 17.5714C21.8571 17.9067 20.7698 18.1786 19.4286 18.1786H2.42857C1.75794 18.1786 1.21429 17.9067 1.21429 17.5714V5.42857C1.21429 4.08731 1.75794 3 2.42857 3C1.08731 3 0 4.08731 0 5.42857V17.5714C0 17.6727 0.00619458 17.7725 0.0182254 17.8704C0.165586 19.0706 1.18855 20 2.42857 20H19.4286C20.7698 20 21.8571 18.9127 21.8571 17.5714Z' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
      userUploads: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='30' height='22' viewBox='0 0 30 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M15.0001 0C12.6659 0 10.3192 0.883288 8.53952 2.649C7.21595 3.96217 6.41366 5.59091 6.07337 7.28954C2.61982 7.95416 0 10.9466 0 14.5585C0 18.6566 3.36935 21.9997 7.5 21.9997H23.4783C27.0744 21.9997 30 19.097 30 15.5291C30 12.1618 27.3854 9.42078 24.0693 9.11921C24.0845 6.77715 23.2461 4.43037 21.4503 2.64865C19.6725 0.884328 17.3343 0 15.0001 0ZM13.903 8.25276C14.0668 8.10404 14.2392 8 14.5654 8C14.8916 8 15.034 8.0765 15.2278 8.25276L18.8148 11.488C19.1908 11.8329 19.2613 12.4885 18.876 12.8631C18.5103 13.2188 17.8695 13.265 17.4901 12.9238L15.5437 11.1647V17.3822C15.5437 17.9182 15.1058 18.3528 14.5654 18.3528C14.0249 18.3528 13.5871 17.9182 13.5871 17.3822V11.1647L11.6407 12.9238C11.2612 13.2648 10.6005 13.2377 10.2547 12.8631C9.909 12.4884 9.90173 11.8694 10.3159 11.488L13.903 8.25276Z' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
      audit: {
        icon: "data:image/svg+xml;charset=UTF-8,%3Csvg width='23' height='23' viewBox='0 0 23 23' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M22.5 11.5C22.5 17.5751 17.5751 22.5 11.5 22.5C5.42487 22.5 0.5 17.5751 0.5 11.5C0.5 5.42487 5.42487 0.5 11.5 0.5C17.5751 0.5 22.5 5.42487 22.5 11.5ZM12.875 6C12.875 6.75939 12.2594 7.375 11.5 7.375C10.7406 7.375 10.125 6.75939 10.125 6C10.125 5.24061 10.7406 4.625 11.5 4.625C12.2594 4.625 12.875 5.24061 12.875 6ZM10.125 10.125C9.36561 10.125 8.75 10.7406 8.75 11.5C8.75 12.2594 9.36561 12.875 10.125 12.875V17C10.125 17.7594 10.7406 18.375 11.5 18.375H12.875C13.6344 18.375 14.25 17.7594 14.25 17C14.25 16.2406 13.6344 15.625 12.875 15.625V11.5C12.875 10.7406 12.2594 10.125 11.5 10.125H10.125Z' fill='%23737373'/%3E%3C/svg%3E%0A",
      },
    },
    user: {
      id: session?.account?.code ?? "",
    },
    locale: currentLocale || "es-AR",
    translations: unlayerMessages,
  });
};
